import { useNavigate } from 'react-router';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type NavigateSafeUrl = string | -1;
export type NavigateSafeFn = (url: NavigateSafeUrl) => void;

export const useNavigateSafe = (): NavigateSafeFn => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return useCallback(
    (navigateToUrl: NavigateSafeUrl) => {
      if (navigateToUrl === -1) {
        navigate(-1);
        return;
      }

      navigate(
        getNavigationUrlWhileKeepingExistingPermalinks({
          navigateToUrl,
          existingSearchParams: searchParams,
        }),
      );
    },
    [navigate, searchParams],
  );
};

/**
 * This function is used to navigate to a new URL while keeping the existing permalinks.
 *
 * This is useful when you want to navigate to a new page while keeping the existing databaseId, organizationId, databaseRevisionId, and dashboardDatabaseRevisionId.
 *
 * @param navigateToUrl - The URL you want to navigate to.
 * @param existingSearchParams - The existing search params from the current URL, which may contain databaseId, organizationId, databaseRevisionId, and dashboardDatabaseRevisionId.
 */
export const getNavigationUrlWhileKeepingExistingPermalinks = ({
  navigateToUrl,
  existingSearchParams,
}: {
  navigateToUrl: string;
  existingSearchParams: URLSearchParams;
}) => {
  const { urlBeforeQuery, hashString, urlQueryString } =
    getComponentsOfNavigationUrl(navigateToUrl);

  const newSearchParams = new URLSearchParams(urlQueryString);

  const existingDbId = existingSearchParams.get('databaseId');
  const existingOrgId = existingSearchParams.get('organizationId');
  const existingDbRevId = existingSearchParams.get('databaseRevisionId');
  const existingDashboardDbRevId = existingSearchParams.get(
    'dashboardDatabaseRevisionId',
  );

  if (existingDbId) {
    newSearchParams.set('databaseId', existingDbId);
  }

  if (existingOrgId) {
    newSearchParams.set('organizationId', existingOrgId);
  }

  if (existingDbRevId) {
    newSearchParams.set('databaseRevisionId', existingDbRevId);
  }

  if (existingDashboardDbRevId) {
    newSearchParams.set(
      'dashboardDatabaseRevisionId',
      existingDashboardDbRevId,
    );
  }

  let finalString = urlBeforeQuery;

  if (Array.from(newSearchParams.keys()).length) {
    finalString += '?' + newSearchParams.toString();
  }

  if (hashString) {
    finalString += '#' + hashString;
  }

  if (finalString.includes(window.location.origin)) {
    finalString = finalString.replace(window.location.origin, '');
  }

  return finalString;
};

export const getComponentsOfNavigationUrl = (navigateToUrl: string) => {
  const queryParamIndex = navigateToUrl.indexOf('?');

  const hashIndex = navigateToUrl.indexOf('#');

  const hashString =
    hashIndex === -1 ? '' : navigateToUrl.substring(hashIndex + 1);

  let earliestExtraIndex = -1;

  if (hashIndex !== -1 && queryParamIndex !== -1) {
    earliestExtraIndex = Math.min(hashIndex, queryParamIndex);
  } else if (hashIndex !== -1) {
    earliestExtraIndex = hashIndex;
  } else if (queryParamIndex !== -1) {
    earliestExtraIndex = queryParamIndex;
  }

  const urlBeforeQuery =
    earliestExtraIndex === -1
      ? navigateToUrl
      : navigateToUrl.substring(0, earliestExtraIndex);

  const urlQueryString =
    queryParamIndex === -1
      ? ''
      : navigateToUrl.substring(
          queryParamIndex + 1,
          hashIndex === -1 ? undefined : hashIndex,
        );

  return {
    urlBeforeQuery,
    hashString,
    urlQueryString,
  };
};
